import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Send, Eye, Mail, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DRAFT_SUBJECT = "Life After the Gates is out now";
const DRAFT_BODY =
  "My new book, Life After the Gates, is out now.\n\nWalking out was only the beginning. This book is about coming home, rebuilding trust, finding work and facing the stigma. The world moved on, and I had to learn how to live in it again.\n\nGet it on its own, or grab both e-books together in the bundle and save.\n\nThank you for your support.";

const field = "w-full rounded border border-white/15 bg-[#1e1e1e] px-3 py-2 text-sm text-[#eee] outline-none focus:border-[#e0a82e]";
const btn = "inline-flex items-center gap-2 rounded px-5 py-2.5 font-display text-sm uppercase tracking-wide transition-colors disabled:opacity-50";

const History = ({ items }) =>
  items.length > 0 && (
    <div className="mt-10" data-testid="announce-history">
      <h2 className="font-display text-xl uppercase tracking-wide text-[#e0a82e]">Sent announcements</h2>
      <ul className="mt-3 divide-y divide-white/10 rounded-md border border-white/10 bg-[#242424]">
        {items.map((h) => (
          <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-testid={`announce-history-${h.id}`}>
            <span className="text-[#e8e8e8]">{h.subject}</span>
            <span className="text-[#9a9a9a]">
              {h.status === "sending" ? "Sending" : "Done"}: {h.sent}/{h.total} sent{h.failed ? `, ${h.failed} failed` : ""} &middot;{" "}
              {new Date(h.createdAt).toLocaleString("en-AU")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

export default function AdminAnnounce() {
  const { user, loading } = useAuth();
  const [subject, setSubject] = useState(DRAFT_SUBJECT);
  const [body, setBody] = useState(DRAFT_BODY);
  const [info, setInfo] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState({ text: "", ok: true });

  const load = useCallback(() => api.get("/admin/announce/info").then(({ data }) => setInfo(data)).catch(() => {}), []);
  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);
  useEffect(() => {
    if (!info?.history?.some((h) => h.status === "sending")) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [info, load]);

  if (loading) return null;
  if (!user?.is_admin) return <Navigate to="/" replace />;

  const run = async (kind) => {
    if (kind === "send" && !window.confirm(`Send "${subject}" to ${info?.recipients ?? 0} people now? This can't be undone.`)) return;
    setBusy(kind); setMsg({ text: "" });
    try {
      const { data } = await api.post(`/admin/announce/${kind}`, { subject, body });
      if (kind === "preview") setPreview(data.html);
      if (kind === "test") setMsg({ text: `Test email sent to ${data.to}.`, ok: true });
      if (kind === "send") { setMsg({ text: `Sending to ${data.total} people. Progress shows below.`, ok: true }); load(); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setMsg({ text: typeof d === "string" ? d : "Please check the subject (3+ characters) and message (10+ characters).", ok: false });
    } finally { setBusy(""); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 md:px-8" data-testid="admin-announce-page">
      <h1 className="font-display text-4xl uppercase tracking-wide text-[#e0a82e]">Email announcement</h1>
      <p className="mt-2 flex items-center gap-2 text-sm text-[#b0b0b0]" data-testid="announce-recipient-count">
        <Mail className="h-4 w-4" /> Goes to {info ? info.recipients : "..."} people (email subscribers and registered members, minus anyone who unsubscribed).
      </p>
      <label className="mt-8 block text-sm text-[#b0b0b0]">Subject</label>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={150} className={`${field} mt-1`} data-testid="announce-subject" />
      <label className="mt-5 block text-sm text-[#b0b0b0]">Message (leave a blank line between paragraphs)</label>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} maxLength={8000} className={`${field} mt-1 resize-y`} data-testid="announce-body" />
      <p className="mt-2 text-xs text-[#8a8a8a]">The email automatically adds the book cover, "Get Life After the Gates" and "Get the Bundle" buttons, and an unsubscribe link.</p>
      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={() => run("preview")} disabled={!!busy} className={`${btn} border border-white/20 text-[#e8e8e8] hover:border-[#e0a82e]`} data-testid="announce-preview-btn">
          {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview
        </button>
        <button onClick={() => run("test")} disabled={!!busy} className={`${btn} border border-[#e0a82e] text-[#e0a82e] hover:bg-[#e0a82e]/10`} data-testid="announce-test-btn">
          {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send test to me
        </button>
        <button onClick={() => run("send")} disabled={!!busy || !info?.recipients} className={`${btn} bg-[#e0a82e] text-[#1c1c1c] hover:bg-[#f2b93f]`} data-testid="announce-send-btn">
          {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to everyone
        </button>
      </div>
      {msg.text && (
        <p className={`mt-4 flex items-center gap-2 text-sm ${msg.ok ? "text-[#9fd59f]" : "text-[#f08a80]"}`} data-testid="announce-message">
          {msg.ok && <CheckCircle2 className="h-4 w-4" />} {msg.text}
        </p>
      )}
      {preview && (
        <div className="mt-8" data-testid="announce-preview">
          <h2 className="mb-3 font-display text-xl uppercase tracking-wide text-[#e0a82e]">Preview</h2>
          <iframe title="Email preview" srcDoc={preview} sandbox="" className="h-[720px] w-full rounded-md border border-white/10 bg-[#333]" />
        </div>
      )}
      <History items={info?.history || []} />
    </div>
  );
}
